import { useEffect, useRef, useState, useMemo } from "react";
import * as d3 from "d3";
import { fileUrl } from "@/lib/api";

/**
 * Family map (D3) with two layouts:
 *   layout="web"  — force-directed graph (default, organic).
 *   layout="tree" — top-down generational tree centered on `self`.
 *
 * Tree layout features:
 *   • Mouse-wheel zoom + click-drag pan over an effectively infinite canvas.
 *   • Subtree-width-aware horizontal layout so siblings cluster under their
 *     parent and no two nodes ever land on the same (x,y).
 *   • Click a node toggles its subtree collapse (when it has descendants).
 *   • Click again (or click the node when no descendants) → onSelect(node).
 *
 * Click a node → onSelect(node).
 */
export default function ConnectionMap({ data, onSelect, layout = "web" }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const [legendOpen, setLegendOpen] = useState(false);

  // Reset collapses when switching layouts or when the data changes shape.
  const dataSignature = useMemo(
    () => (data?.nodes || []).map((n) => n.id).sort().join("|"),
    [data],
  );
  useEffect(() => {
    setCollapsedIds(new Set());
  }, [dataSignature, layout]);

  useEffect(() => {
    if (!data || !data.nodes) return;
    const container = containerRef.current;
    const width = container?.clientWidth || 900;
    const height = 600;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("viewBox", [0, 0, width, height]);

    const nodes = data.nodes.map((n) => ({ ...n }));
    const realLinks = data.edges.map((e) => ({ ...e }));

    // Synthetic edges derived from placeholder.parent_* fields. The
    // relationship label on the placeholder decides which generation it lands
    // on relative to its "Connected to" node. Examples:
    //   • Cornelius (Son) connected to Sarah → Cornelius sits one row BELOW.
    //   • Sarah (Mother) connected to Me → Sarah sits one row ABOVE.
    //   • Joe (Brother) connected to Me → Joe sits at the SAME level.
    // `gen_delta` is signed: negative = above, positive = below, 0 = same row.
    const RELATIONSHIP_GEN_DELTA = {
      father: -1, mother: -1,
      grandfather: -2, grandmother: -2,
      "great-grandfather": -3, "great-grandmother": -3,
      son: 1, daughter: 1,
      grandson: 2, granddaughter: 2, grandchild: 2,
      "great-grandchild": 3,
      nephew: 1, niece: 1,
      // Per spec: uncle / aunt / brother / sister / spouse / cousin render
      // at the same level as their connected node (avoids accidentally lifting
      // them above when the user is mapping side-relations).
      uncle: 0, aunt: 0,
      brother: 0, sister: 0,
      // Phase 1 simplified role — "Sibling" must render on the same generation
      // row as its connected node (not one below it).
      sibling: 0,
      spouse: 0, cousin: 0,
    };
    const genDeltaFor = (label) => {
      const key = (label || "").trim().toLowerCase();
      if (key in RELATIONSHIP_GEN_DELTA) return RELATIONSHIP_GEN_DELTA[key];
      // Unknown / free-text role (org seats): default to "below" so the seat
      // still slots beneath the node it reports to.
      return 1;
    };

    const nodeIndex = new Map(nodes.map((n) => [n.id, n]));
    const syntheticLinks = [];
    const pushSynthetic = (sourceId, targetId, n) => {
      if (!targetId || !nodeIndex.has(targetId) || targetId === sourceId) return;
      const dup = realLinks.find(
        (e) =>
          (e.source === sourceId && e.target === targetId) ||
          (e.source === targetId && e.target === sourceId),
      );
      if (dup) return;
      const already = syntheticLinks.find(
        (e) => e.source === sourceId && e.target === targetId,
      );
      if (already) return;
      syntheticLinks.push({
        source: sourceId,
        target: targetId,
        // Logical type for downstream defaults; gen_delta is the source of truth.
        type: "synthetic",
        synthetic: true,
        gen_delta: genDeltaFor(n.role_or_relationship || n.relationship_type),
      });
    };
    // Fix 3 helper — walk UP the placeholder graph to find a node's genealogical
    // parent. The `parent_*_id` field is just the "Connected to" anchor and
    // its meaning depends on the role: for Son/Daughter the anchor IS the
    // parent; for Mother/Father the anchor is the CHILD; for Sibling/Spouse
    // the anchor is a same-gen relative. To find a Mother/Father-role node's
    // actual parent we look for ANOTHER node whose role is Mother/Father AND
    // whose anchor points back at our target.
    const findGenealogicalParent = (id) => {
      const node = nodeIndex.get(id);
      if (!node) return null;
      const role = (node.role_or_relationship || node.relationship_type || "").toLowerCase();
      if (role === "son" || role === "daughter") {
        return node.parent_user_id || node.parent_placeholder_id || null;
      }
      // Mother/Father/Sibling/Spouse: scan for a Mother/Father placeholder
      // that has the target as its anchor (i.e. the target is THAT
      // placeholder's child).
      for (const m of nodes) {
        if (!(m.is_placeholder || m.is_living)) continue;
        const mRole = (m.role_or_relationship || m.relationship_type || "").toLowerCase();
        if (mRole !== "mother" && mRole !== "father") continue;
        const mAnchor = m.parent_user_id || m.parent_placeholder_id;
        if (mAnchor === id) return m.id;
      }
      return null;
    };
    for (const n of nodes) {
      const isPlaceholder = n.is_placeholder || n.is_living;
      if (!isPlaceholder) continue;
      pushSynthetic(n.id, n.parent_user_id || n.parent_placeholder_id, n);
      // Second-parent edge (Fix 4 — blended families). Drawn as an extra
      // synthetic link so the child renders between both parents on the tree.
      pushSynthetic(n.id, n.parent2_user_id || n.parent2_placeholder_id, n);
      // Fix 3 — Sibling-role placeholders chain through their anchor's
      // GENEALOGICAL parent (not the anchor itself) so they also visually
      // hang off the shared grandparent.
      const roleLower = (n.role_or_relationship || n.relationship_type || "").toLowerCase();
      if (roleLower === "sibling") {
        const anchorId = n.parent_placeholder_id || n.parent_user_id;
        if (anchorId) {
          const grandparentId = findGenealogicalParent(anchorId);
          if (grandparentId && grandparentId !== n.id && nodeIndex.has(grandparentId)) {
            const dup = realLinks.find(
              (e) =>
                (e.source === n.id && e.target === grandparentId) ||
                (e.source === grandparentId && e.target === n.id),
            );
            const already = syntheticLinks.find(
              (e) => e.source === n.id && e.target === grandparentId,
            );
            if (!dup && !already) {
              // gen_delta = +1 means the source (sibling) sits one row BELOW
              // the target (grandparent) — same effect as a real Daughter/Son
              // role edge.
              syntheticLinks.push({
                source: n.id,
                target: grandparentId,
                type: "synthetic",
                synthetic: true,
                gen_delta: 1,
              });
            }
          }
        }
      }
    }
    const links = [...realLinks, ...syntheticLinks];
    const root = svg.append("g");
    // SVG <defs>: blur filter used by the memorial halo glow.
    const defs = svg.append("defs");
    defs
      .append("filter")
      .attr("id", "legacy-halo-blur")
      .attr("x", "-50%").attr("y", "-50%")
      .attr("width", "200%").attr("height", "200%")
      .append("feGaussianBlur")
      .attr("stdDeviation", 3.5);
    svg.call(
      d3.zoom().scaleExtent([0.2, 4]).on("zoom", (ev) => {
        root.attr("transform", ev.transform);
      })
    );

    const linkColor = (d) =>
      d.type === "spouse" ? "#D4AF37" : d.type === "sibling" ? "#9AA39B" : "#1B3A2C";

    // Children map (populated in the tree branch). Shared with the node
    // rendering pass so we can draw a collapse handle on every parent.
    const childrenOf = new Map();

    let link;
    let sim;

    if (layout === "tree") {
      // -------- Generational top-down layout --------
      // 1. Assign generation levels relative to `self`.
      //    parent edge (source claims target as parent) → gen(source) = gen(target) + 1
      //    child  edge → gen(target) = gen(source) + 1
      //    grandparent → gen(source) = gen(target) + 2
      //    grandchild  → gen(target) = gen(source) + 2
      //    spouse / sibling → same generation
      const generation = {};
      const selfNode = nodes.find((n) => n.is_self);
      if (selfNode) generation[selfNode.id] = 0;
      else if (nodes[0]) generation[nodes[0].id] = 0;

      const deltaFor = (type, dir) => {
        // dir=+1 means we're computing target from source, -1 the other way.
        if (type === "parent") return dir === 1 ? -1 : 1;
        if (type === "child") return dir === 1 ? 1 : -1;
        if (type === "grandparent") return dir === 1 ? -2 : 2;
        if (type === "grandchild") return dir === 1 ? 2 : -2;
        return 0; // spouse, sibling — same gen
      };
      // For synthetic placeholder edges the relationship label decides the
      // generation offset of the placeholder (source) relative to its
      // connected node (target). Translate that into the same source→target
      // delta deltaFor() returns for real edges.
      const synthDelta = (e, dir) => {
        const d = e.gen_delta || 0;
        return dir === 1 ? -d : d;
      };
      // Relax until stable.
      let changed = true;
      let iter = 0;
      while (changed && iter < 50) {
        changed = false;
        iter += 1;
        for (const e of links) {
          const s = e.source;
          const t = e.target;
          if (s in generation && !(t in generation)) {
            generation[t] = generation[s] + (e.synthetic ? synthDelta(e, 1) : deltaFor(e.type, 1));
            changed = true;
          } else if (t in generation && !(s in generation)) {
            generation[s] = generation[t] + (e.synthetic ? synthDelta(e, -1) : deltaFor(e.type, -1));
            changed = true;
          }
        }
      }
      // Place disconnected nodes at gen 0 (visualized as a separate column row).
      nodes.forEach((n) => {
        if (!(n.id in generation)) generation[n.id] = 0;
        n.gen = generation[n.id];
      });

      // 2. Subtree-width-aware horizontal layout.
      //    a) Build parent→children map. For real "parent" edges, child is
      //       the source. For "child" edges, child is the target. For
      //       synthetic placeholder edges, gen_delta < 0 means the
      //       placeholder is the parent; > 0 means it's the child; 0 means
      //       same row (treated as sibling, not parent/child).
      const parentsOf = new Map();
      const addEdge = (parentId, childId) => {
        if (!parentId || !childId || parentId === childId) return;
        if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
        if (!childrenOf.get(parentId).includes(childId)) childrenOf.get(parentId).push(childId);
        if (!parentsOf.has(childId)) parentsOf.set(childId, []);
        if (!parentsOf.get(childId).includes(parentId)) parentsOf.get(childId).push(parentId);
      };
      for (const e of links) {
        const s = e.source?.id ?? e.source;
        const t = e.target?.id ?? e.target;
        if (e.synthetic) {
          const d = e.gen_delta || 0;
          if (d < 0) addEdge(s, t);
          else if (d > 0) addEdge(t, s);
        } else if (e.type === "parent") {
          addEdge(t, s);   // s claims t as parent → t is parent of s
        } else if (e.type === "child") {
          addEdge(s, t);
        } else if (e.type === "grandparent") {
          addEdge(t, s);   // not a direct child but treated similarly for layout
        } else if (e.type === "grandchild") {
          addEdge(s, t);
        }
      }
      // Helper: is a node a descendant of any collapsed node?
      const isHidden = (id) => {
        const visit = (cur, seen) => {
          for (const p of parentsOf.get(cur) || []) {
            if (seen.has(p)) continue;
            seen.add(p);
            if (collapsedIds.has(p)) return true;
            if (visit(p, seen)) return true;
          }
          return false;
        };
        return visit(id, new Set());
      };
      // Sort siblings deterministically: self first, then by name.
      const sortKids = (kids) =>
        kids
          .slice()
          .sort((a, b) => {
            const na = nodeIndex.get(a);
            const nb = nodeIndex.get(b);
            if (!na || !nb) return 0;
            if (na.is_self) return -1;
            if (nb.is_self) return 1;
            return (na.name || "").localeCompare(nb.name || "");
          });
      // Subtree-width recursive computation. Width is measured in slots.
      const SLOT_W = 165;
      const ROW_H = 150;
      const widthCache = new Map();
      const subtreeWidth = (id, visited) => {
        if (widthCache.has(id)) return widthCache.get(id);
        if (visited.has(id)) return 1;
        visited.add(id);
        if (collapsedIds.has(id)) { widthCache.set(id, 1); return 1; }
        const kids = sortKids(childrenOf.get(id) || []);
        if (kids.length === 0) { widthCache.set(id, 1); return 1; }
        let w = 0;
        for (const c of kids) w += subtreeWidth(c, visited);
        const total = Math.max(1, w);
        widthCache.set(id, total);
        return total;
      };
      // Identify roots: topmost generation, sorted so self is centered when in row.
      const visibleNodes = nodes.filter((n) => !isHidden(n.id));
      const minGen = visibleNodes.length
        ? Math.min(...visibleNodes.map((n) => n.gen))
        : 0;
      const rootIds = visibleNodes
        .filter((n) => n.gen === minGen)
        .sort((a, b) => {
          if (a.is_self) return -1;
          if (b.is_self) return 1;
          return (a.name || "").localeCompare(b.name || "");
        })
        .map((n) => n.id);
      // Top-down layout from each root.
      const placed = new Set();
      const placeNode = (id, leftSlot, gen) => {
        if (placed.has(id)) return;
        const w = subtreeWidth(id, new Set());
        const n = nodeIndex.get(id);
        if (n) {
          n.x = (leftSlot + w / 2) * SLOT_W;
          n.y = (gen - minGen) * ROW_H + 60;
          placed.add(id);
        }
        if (collapsedIds.has(id)) return;
        const kids = sortKids(childrenOf.get(id) || []);
        let cursor = leftSlot;
        for (const c of kids) {
          const cw = subtreeWidth(c, new Set());
          const childGen = (nodeIndex.get(c)?.gen) ?? gen + 1;
          placeNode(c, cursor, childGen);
          cursor += cw;
        }
      };
      let slotCursor = 0;
      // Fix 4 — Sibling roots cluster: when two adjacent roots are connected
      // by a sibling edge (real or synthetic d=0), keep their CENTERS at
      // exactly one slot apart regardless of subtree width. The wide-
      // descendant gap (Sarah Williams pushed far from her siblings, or
      // Grandma → GranSib3 gap of 396px) goes away.
      const isSiblingPair = (idA, idB) => {
        if (!idA || !idB || idA === idB) return false;
        // Edge-based check (real or synthetic d=0).
        if (links.some((e) => {
          const s = e.source?.id ?? e.source;
          const t = e.target?.id ?? e.target;
          const matches = (s === idA && t === idB) || (s === idB && t === idA);
          if (!matches) return false;
          if (e.synthetic) return (e.gen_delta || 0) === 0;
          return e.type === "sibling";
        })) return true;
        // Role-based check for unclaimed Sibling-role placeholders: they
        // synthetic-connect via a 0-delta edge already, but be defensive.
        const a = nodeIndex.get(idA);
        const b = nodeIndex.get(idB);
        const roleA = (a?.role_or_relationship || "").toLowerCase();
        const roleB = (b?.role_or_relationship || "").toLowerCase();
        const anchorA = a?.parent_user_id || a?.parent_placeholder_id;
        const anchorB = b?.parent_user_id || b?.parent_placeholder_id;
        if (roleA === "sibling" && roleB === "sibling" && anchorA && anchorA === anchorB) return true;
        return false;
      };
      // Pre-cluster rootIds: keep siblings adjacent so the row-by-row walk
      // below sees them as neighbors. We seed each cluster with a non-sibling
      // root (or `self` when present at the root row) and pull in any roots
      // that share a sibling edge with someone already in the cluster.
      const clusteredRootIds = [];
      const remaining = [...rootIds];
      while (remaining.length) {
        const seed = remaining.shift();
        const cluster = [seed];
        let grew = true;
        while (grew) {
          grew = false;
          for (let i = remaining.length - 1; i >= 0; i -= 1) {
            const cand = remaining[i];
            if (cluster.some((cid) => isSiblingPair(cid, cand))) {
              cluster.push(cand);
              remaining.splice(i, 1);
              grew = true;
            }
          }
        }
        clusteredRootIds.push(...cluster);
      }
      // Track the center (in SLOTS) of the previously placed root so that a
      // sibling root sits at prevCenter + 1 — no matter how wide its subtree.
      let prevCenterSlot = null;
      for (let i = 0; i < clusteredRootIds.length; i += 1) {
        const rid = clusteredRootIds[i];
        const prev = i > 0 ? clusteredRootIds[i - 1] : null;
        const w = subtreeWidth(rid, new Set());
        let leftSlot;
        if (prev && isSiblingPair(prev, rid) && prevCenterSlot != null) {
          // Center the current root one slot to the right of the previous
          // root's center — collisions in the descendant rows are resolved by
          // the overlap pass below.
          const targetCenter = prevCenterSlot + 1;
          leftSlot = targetCenter - w / 2;
        } else {
          leftSlot = slotCursor;
        }
        placeNode(rid, leftSlot, minGen);
        prevCenterSlot = leftSlot + w / 2;
        // Advance slotCursor only when we used the subtree-width branch,
        // OR when the next root is NOT a sibling of this one (so disjoint
        // families keep their gap).
        const next = i + 1 < clusteredRootIds.length ? clusteredRootIds[i + 1] : null;
        const nextIsSibling = next && isSiblingPair(rid, next);
        if (!nextIsSibling) {
          slotCursor = Math.max(slotCursor, leftSlot + w) + 0.4;
        }
      }
      // Any visible node that didn't land via the root walk (cross-edge spouses
      // missing parent path, orphans, etc.) — anchor next to a buddy
      // (spouse/sibling) or append at the right edge of its generation row.
      // Fix 3 — process SPOUSE buddies first, then siblings, so spouse always
      // sits immediately adjacent to the anchor (siblings of the anchor get
      // pushed to the OUTER edge of the owner+spouse cluster).
      // Helper: recursively place a node's descendants (childrenOf walk)
      // centered under the node's already-set x. Used by buddy placement so
      // siblings of a placed node bring their families with them rather than
      // leaving them stranded on the right edge of the canvas. (Fix 2)
      const placeSubtreeUnderAnchor = (id, anchorX) => {
        const kids = sortKids(childrenOf.get(id) || []).filter((k) => !placed.has(k));
        if (!kids.length) return;
        let totalSlots = 0;
        for (const c of kids) totalSlots += subtreeWidth(c, new Set());
        let cursorX = anchorX - (totalSlots * SLOT_W) / 2;
        for (const c of kids) {
          const cw = subtreeWidth(c, new Set());
          const cn = nodeIndex.get(c);
          if (!cn) continue;
          const cCenterX = cursorX + (cw * SLOT_W) / 2;
          const cGen = cn.gen ?? minGen + 1;
          cn.x = cCenterX;
          cn.y = (cGen - minGen) * ROW_H + 60;
          placed.add(c);
          placeSubtreeUnderAnchor(c, cCenterX);
          cursorX += cw * SLOT_W;
        }
      };
      // Any visible node that didn't land via the root walk (cross-edge spouses
      // missing parent path, orphans, etc.) — anchor next to a buddy
      // (spouse/sibling) or append at the right edge of its generation row.
      // Fix 3 — process SPOUSE buddies first, then siblings, so spouse always
      // sits immediately adjacent to the anchor (siblings of the anchor get
      // pushed to the OUTER edge of the owner+spouse cluster).
      const placeViaBuddy = (n, allowedTypes) => {
        const buddy = links.find((e) => {
          const isSibAllowed = allowedTypes.includes("sibling");
          const isSpouseAllowed = allowedTypes.includes("spouse");
          // Synthetic edges with gen_delta=0 represent both Sibling AND Spouse
          // placeholder anchors. Without this, real members like Sarah's
          // siblings (Emma, Rebe, …) couldn't find their buddy anchor.
          if (e.synthetic) {
            if (!isSibAllowed && !isSpouseAllowed) return false;
            if ((e.gen_delta || 0) !== 0) return false;
          } else if (!allowedTypes.includes(e.type)) {
            return false;
          }
          const s = e.source?.id ?? e.source;
          const t = e.target?.id ?? e.target;
          if (s === n.id && placed.has(t)) return true;
          if (t === n.id && placed.has(s)) return true;
          return false;
        });
        if (!buddy) return false;
        const s = buddy.source?.id ?? buddy.source;
        const t = buddy.target?.id ?? buddy.target;
        const buddyId = s === n.id ? t : s;
        const bn = nodeIndex.get(buddyId);
        if (!bn || bn.x == null || bn.y == null) return false;
        // Fix 1 — sit exactly one SLOT to the right of the buddy. The
        // collision pass below redistributes if two siblings collide.
        n.x = bn.x + SLOT_W;
        n.y = bn.y;
        placed.add(n.id);
        // Fix 2 — bring this node's family with it: descendants center under
        // its new x. Without this, Bobby Harris (child of buddy-placed Emma)
        // would land at slotCursor far away from his parent.
        placeSubtreeUnderAnchor(n.id, n.x);
        return true;
      };
      for (const n of visibleNodes) {
        if (placed.has(n.id)) continue;
        if (placeViaBuddy(n, ["spouse"])) continue;
      }
      for (const n of visibleNodes) {
        if (placed.has(n.id)) continue;
        if (placeViaBuddy(n, ["sibling"])) continue;
        n.x = (slotCursor + 0.5) * SLOT_W;
        n.y = ((n.gen ?? minGen) - minGen) * ROW_H + 60;
        placed.add(n.id);
        slotCursor += 1;
      }
      // Fix 3 — Spouse adjacency enforcement: for every (anchor, spouse) pair
      // on the same row, find any sibling-of-anchor that landed BETWEEN them
      // and push the sibling to the outer edge of the anchor+spouse cluster.
      // Spouse can be either (a) a real spouse-typed edge, or (b) an unclaimed
      // placeholder whose role_or_relationship is "Spouse" anchored to the
      // partner via parent_user_id / parent_placeholder_id (those project as
      // synthetic d=0 edges, just like Sibling, so we can't filter by type).
      const isSpousePair = (idA, idB) => {
        if (!idA || !idB || idA === idB) return false;
        if (links.some((e) => {
          const s = e.source?.id ?? e.source;
          const t = e.target?.id ?? e.target;
          const matches = (s === idA && t === idB) || (s === idB && t === idA);
          if (!matches) return false;
          return e.type === "spouse";
        })) return true;
        const a = nodeIndex.get(idA);
        const b = nodeIndex.get(idB);
        const roleA = (a?.role_or_relationship || "").toLowerCase();
        const roleB = (b?.role_or_relationship || "").toLowerCase();
        const anchorA = a?.parent_user_id || a?.parent_placeholder_id;
        const anchorB = b?.parent_user_id || b?.parent_placeholder_id;
        // Placeholder Spouse pointing at the partner.
        if (roleA === "spouse" && anchorA === idB) return true;
        if (roleB === "spouse" && anchorB === idA) return true;
        return false;
      };
      // Build (anchor, spouse) pairs by scanning all nodes for the relations
      // above. We collect both directions so the post-pass is symmetric.
      const spousePairs = [];
      for (let i = 0; i < visibleNodes.length; i += 1) {
        for (let j = i + 1; j < visibleNodes.length; j += 1) {
          if (isSpousePair(visibleNodes[i].id, visibleNodes[j].id)) {
            spousePairs.push([visibleNodes[i], visibleNodes[j]]);
          }
        }
      }
      for (const [a, b] of spousePairs) {
        if (a.x == null || b.x == null) continue;
        if (Math.abs(a.y - b.y) > ROW_H / 2) continue;
        const [left, right] = a.x < b.x ? [a, b] : [b, a];
        // Force spouse to sit exactly one slot to the right of the anchor.
        right.x = left.x + SLOT_W;
        // Push any sibling-of-either that landed BETWEEN them out to the
        // outer edge of the couple's bracket.
        for (const n of visibleNodes) {
          if (n.id === left.id || n.id === right.id) continue;
          if (n.x == null || n.y == null) continue;
          if (Math.abs(n.y - left.y) > ROW_H / 2) continue;
          if (n.x <= left.x || n.x >= right.x) continue;
          if (!isSiblingPair(n.id, left.id) && !isSiblingPair(n.id, right.id)) continue;
          n.x = right.x + SLOT_W;
        }
      }

      // 2.b Overlap resolution pass — guarantees no two visible nodes land
      // on the same (row, x). For each generation row, sort by x and push
      // overlapping nodes (along with their entire subtree) to the right.
      // This catches edge cases the bottom-up subtree-width pass can miss:
      // single-child families collapsing onto siblings, buddy-anchored
      // orphans landing on top of an existing subtree, etc.
      const MIN_X_GAP = SLOT_W * 0.85;
      // Tolerance for "same row" so that floating-point drift or sub-row
      // nudges still bucket together. Anything within ROW_H/3 is one row.
      const ROW_BUCKET = ROW_H / 3;
      const shiftSubtree = (id, dx, seenShift) => {
        if (!dx) return;
        if (seenShift.has(id)) return;
        seenShift.add(id);
        const sn = nodeIndex.get(id);
        if (!sn) return;
        sn.x += dx;
        for (const c of childrenOf.get(id) || []) shiftSubtree(c, dx, seenShift);
      };
      // Bucket nodes into discrete rows by Math.round(y / ROW_BUCKET) so
      // near-equal y values (visual same row) get processed together. We
      // run the row sweep multiple times so cascading shifts settle.
      for (let pass = 0; pass < 3; pass += 1) {
        const byRowBucket = new Map();
        for (const n of visibleNodes) {
          if (n.x == null) continue;
          const bucket = Math.round(n.y / ROW_BUCKET);
          if (!byRowBucket.has(bucket)) byRowBucket.set(bucket, []);
          byRowBucket.get(bucket).push(n);
        }
        let anyShift = false;
        for (const rowNodes of byRowBucket.values()) {
          rowNodes.sort((a, b) => a.x - b.x);
          for (let i = 1; i < rowNodes.length; i++) {
            const prev = rowNodes[i - 1];
            const cur = rowNodes[i];
            const overlap = MIN_X_GAP - (cur.x - prev.x);
            if (overlap > 0) {
              shiftSubtree(cur.id, overlap, new Set());
              anyShift = true;
            }
          }
        }
        if (!anyShift) break;
      }
      // 2.c FINAL hard collision guard — after all passes, walk the entire
      // node set and ensure no two nodes share (x, y) within a small radius
      // regardless of generation. This catches synthetic cross-row links
      // and orphan placements that might still collide.
      const HARD_RADIUS = 70;       // < node diameter; tightens to absolute no-overlap
      const HARD_X_PUSH = SLOT_W * 0.5;
      const positioned = visibleNodes.filter((n) => n.x != null && n.y != null);
      for (let guard = 0; guard < 4; guard += 1) {
        let anyHardShift = false;
        for (let i = 0; i < positioned.length; i++) {
          for (let j = i + 1; j < positioned.length; j++) {
            const a = positioned[i];
            const b = positioned[j];
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            if (Math.abs(dx) < HARD_RADIUS && Math.abs(dy) < HARD_RADIUS) {
              // Push b (and its subtree) to the right.
              const push = HARD_RADIUS - Math.abs(dx) + HARD_X_PUSH;
              shiftSubtree(b.id, push, new Set());
              anyHardShift = true;
            }
          }
        }
        if (!anyHardShift) break;
      }

      // Filter to visible nodes (descendants of collapsed parents are hidden).
      // Links between two visible nodes render; others drop.
      const visibleSet = new Set(visibleNodes.map((n) => n.id));
      const treeLinks = links.filter((e) => {
        const s = e.source?.id ?? e.source;
        const t = e.target?.id ?? e.target;
        return visibleSet.has(s) && visibleSet.has(t);
      });

      // 3. Draw soft horizontal sibling-group connector underneath every pair
      //    of nodes that share the same parent AND sit on the same y-row.
      //    This makes same-generation groupings visually obvious without
      //    relying on the sibling edge alone.
      const siblingBars = [];
      for (const kids of childrenOf.values()) {
        const positionedKids = kids
          .map((cid) => nodeIndex.get(cid))
          .filter((n) => n && n.x != null && n.y != null && visibleSet.has(n.id));
        if (positionedKids.length < 2) continue;
        // Bucket by y so a single sibling-group on the same row gets one bar
        // (cross-row children — rare with current layout — get separate bars).
        const byRow = new Map();
        for (const n of positionedKids) {
          const key = Math.round(n.y);
          if (!byRow.has(key)) byRow.set(key, []);
          byRow.get(key).push(n);
        }
        for (const rowKids of byRow.values()) {
          if (rowKids.length < 2) continue;
          rowKids.sort((a, b) => a.x - b.x);
          siblingBars.push({
            x1: rowKids[0].x,
            x2: rowKids[rowKids.length - 1].x,
            y: rowKids[0].y + 38, // sits just below the node circles
          });
        }
      }
      root
        .append("g")
        .attr("class", "sibling-connectors")
        .selectAll("line")
        .data(siblingBars)
        .join("line")
        .attr("x1", (d) => d.x1)
        .attr("x2", (d) => d.x2)
        .attr("y1", (d) => d.y)
        .attr("y2", (d) => d.y)
        .attr("stroke", "#9AA39B")
        .attr("stroke-opacity", 0.45)
        .attr("stroke-width", 1)
        .attr("stroke-linecap", "round");

      // 4. Draw orthogonal links for parent/child, straight lines for spouse/sibling.
      link = root
        .append("g")
        .selectAll("path")
        .data(treeLinks)
        .join("path")
        .attr("fill", "none")
        .attr("stroke", (d) => (d.synthetic ? "#E89C3F" : linkColor(d)))
        .attr("stroke-opacity", (d) => (d.synthetic ? 0.55 : 0.6))
        .attr("stroke-width", 1.6)
        .attr("stroke-dasharray", (d) => {
          if (d.synthetic) return "5 4";
          if (d.type === "sibling") return "4 4";
          return "0";
        })
        .attr("d", (d) => {
          const sn = nodeIndex.get(d.source?.id ?? d.source);
          const tn = nodeIndex.get(d.target?.id ?? d.target);
          if (!sn || !tn) return "";
          const isSameRow = d.synthetic
            ? (d.gen_delta || 0) === 0
            : (d.type === "spouse" || d.type === "sibling");
          if (!isSameRow && (d.synthetic || d.type === "parent" || d.type === "child" || d.type === "grandparent" || d.type === "grandchild")) {
            // Orthogonal: vertical from upper to midpoint, then horizontal, then vertical down.
            const upper = sn.y < tn.y ? sn : tn;
            const lower = sn.y < tn.y ? tn : sn;
            const midY = (upper.y + lower.y) / 2;
            return `M${upper.x},${upper.y + 30} V${midY} H${lower.x} V${lower.y - 30}`;
          }
          // spouse / sibling / same-row synthetic — straight line
          const left = sn.x < tn.x ? sn : tn;
          const right = sn.x < tn.x ? tn : sn;
          return `M${left.x + 30},${left.y} L${right.x - 30},${right.y}`;
        });
      // Track which nodes are render-eligible for the node rendering pass below.
      nodes.length = 0;
      nodes.push(...visibleNodes);
    } else {
      // -------- Force-directed web layout --------
      sim = d3
        .forceSimulation(nodes)
        .force("link", d3.forceLink(links).id((d) => d.id).distance(140).strength(0.6))
        .force("charge", d3.forceManyBody().strength(-380))
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force("collision", d3.forceCollide().radius(46));

      link = root
        .append("g")
        .selectAll("line")
        .data(links)
        .join("line")
        .attr("stroke", linkColor)
        .attr("stroke-opacity", 0.55)
        .attr("stroke-width", 1.6)
        .attr("stroke-dasharray", (d) => (d.type === "sibling" ? "4 4" : "0"));
    }

    const node = root
      .append("g")
      .selectAll("g")
      .data(nodes)
      .join("g")
      .attr("class", "cursor-pointer")
      .style("cursor", "pointer")
      .on("click", (_, d) => onSelect?.(d))
      .call(
        layout === "web"
          ? d3.drag()
              .on("start", (event, d) => { if (!event.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
              .on("drag", (event, d) => { d.fx = event.x; d.fy = event.y; })
              .on("end", (event, d) => { if (!event.active) sim.alphaTarget(0); d.fx = null; d.fy = null; })
          : () => {}
      );

    // outer ring — color encodes branch / memorial / living / self
    const ringColor = (d) => {
      if (d.is_memorial) return "#E89C3F";  // gold halo accent for memorials
      if (d.is_living) return "#E89C3F";
      if (d.is_self) return "#D4AF37";
      if (d.branch === "paternal") return "#D4AF37";
      if (d.branch === "maternal") return "#1B3A2C";
      return "#9AA39B";
    };
    // Soft glowing halo for memorial nodes — a wide outer ring with a
    // Gaussian blur filter so it reads as a respectful golden glow rather
    // than a hard border. Inserted UNDER the main circle.
    node
      .filter((d) => d.is_memorial)
      .each(function () {
        const g = d3.select(this);
        // Outer soft glow (blurred).
        g.append("circle")
          .attr("class", "memorial-halo-glow")
          .attr("r", 40)
          .attr("fill", "none")
          .attr("stroke", "#E89C3F")
          .attr("stroke-width", 6)
          .attr("opacity", 0.45)
          .attr("filter", "url(#legacy-halo-blur)");
        // Inner crisp halo ring (no blur) to anchor the glow.
        g.append("circle")
          .attr("class", "memorial-halo-ring")
          .attr("r", 35)
          .attr("fill", "none")
          .attr("stroke", "#FFFFFF")
          .attr("stroke-width", 1.5)
          .attr("opacity", 0.9);
      });
    node
      .append("circle")
      .attr("r", 30)
      .attr("fill", (d) => (d.is_memorial ? "#FFF8EC" : d.is_living ? "#FFF8EC" : "#FFFFFF"))
      .attr("stroke", ringColor)
      .attr("stroke-width", (d) => (d.is_self ? 3 : d.is_memorial ? 2 : d.is_living ? 2.5 : 2))
      .attr("stroke-dasharray", "0");

    // memorial year pill ("1918 – 2002")
    const memorialNodes = node.filter((d) => d.is_memorial && (d.birth_year || d.death_year));
    memorialNodes.each(function (d) {
      const g = d3.select(this);
      const label = `${d.birth_year ?? ""} – ${d.death_year ?? ""}`.trim();
      const padX = 7, padY = 3;
      const pill = g.append("g").attr("class", "memorial-year-pill").attr("transform", "translate(0,-40)");
      const rect = pill.append("rect").attr("fill", "#FFFFFF").attr("stroke", "#A8A8B0").attr("stroke-width", 1).attr("rx", 8).attr("ry", 8);
      const text = pill.append("text").text(label).attr("text-anchor", "middle").attr("dy", "0.35em").attr("fill", "#5A605B").style("font-family", "Manrope, sans-serif").style("font-size", "10px").style("font-weight", 600).style("letter-spacing", "0.04em");
      const bbox = text.node().getBBox();
      rect.attr("x", -bbox.width / 2 - padX).attr("y", -bbox.height / 2 - padY).attr("width", bbox.width + padX * 2).attr("height", bbox.height + padY * 2);
    });

    // "Not yet joined" amber pill
    const livingNodes = node.filter((d) => d.is_living);
    livingNodes.each(function () {
      const g = d3.select(this);
      const padX = 7, padY = 3;
      const pill = g.append("g").attr("class", "living-pill").attr("transform", "translate(0,-40)");
      const rect = pill.append("rect").attr("fill", "#FFF6E5").attr("stroke", "#E89C3F").attr("stroke-width", 1).attr("rx", 8).attr("ry", 8);
      const text = pill.append("text").text("Not yet joined").attr("text-anchor", "middle").attr("dy", "0.35em").attr("fill", "#8A5A1F").style("font-family", "Manrope, sans-serif").style("font-size", "10px").style("font-weight", 600).style("letter-spacing", "0.04em");
      const bbox = text.node().getBBox();
      rect.attr("x", -bbox.width / 2 - padX).attr("y", -bbox.height / 2 - padY).attr("width", bbox.width + padX * 2).attr("height", bbox.height + padY * 2);
    });

    // inner photo or initial
    node.each(function (d) {
      const g = d3.select(this);
      if (d.profile_photo_path) {
        g.append("clipPath").attr("id", `clip-${d.id}`).append("circle").attr("r", 26);
        g.append("image").attr("href", fileUrl(d.profile_photo_path)).attr("x", -26).attr("y", -26).attr("width", 52).attr("height", 52).attr("clip-path", `url(#clip-${d.id})`);
      } else {
        g.append("circle").attr("r", 26).attr("fill", "#1B3A2C");
        g.append("text").text(d.name?.charAt(0).toUpperCase() || "?").attr("text-anchor", "middle").attr("dy", "0.35em").attr("fill", "#D4AF37").style("font-family", "Playfair Display, serif").style("font-size", "20px");
      }
    });

    // labels
    node.append("text").text((d) => d.name).attr("text-anchor", "middle").attr("y", 46).attr("fill", "#1A1C1A").style("font-family", "Manrope, sans-serif").style("font-size", "12px").style("font-weight", 600);
    node.append("text").text((d) => d.relationship).attr("text-anchor", "middle").attr("y", 60).attr("fill", "#5A605B").style("font-family", "Manrope, sans-serif").style("font-size", "11px");

    // "In Memoriam" pill below the name for memorial nodes. Uses a muted
    // dark forest pill so it reads as solemn but is the same shape language
    // as the "Not yet joined" pill above placeholder nodes.
    node
      .filter((d) => d.is_memorial)
      .each(function () {
        const g = d3.select(this);
        const padX = 7, padY = 3;
        const pill = g.append("g").attr("class", "memoriam-pill").attr("transform", "translate(0,76)");
        const rect = pill.append("rect")
          .attr("fill", "#1B3A2C")
          .attr("stroke", "#D4AF37")
          .attr("stroke-width", 1)
          .attr("opacity", 0.92)
          .attr("rx", 8).attr("ry", 8);
        const text = pill.append("text")
          .text("In Memoriam")
          .attr("text-anchor", "middle")
          .attr("dy", "0.35em")
          .attr("fill", "#F4ECD4")
          .style("font-family", "Manrope, sans-serif")
          .style("font-size", "9px")
          .style("font-weight", 600)
          .style("letter-spacing", "0.12em")
          .style("text-transform", "uppercase");
        const bbox = text.node().getBBox();
        rect.attr("x", -bbox.width / 2 - padX).attr("y", -bbox.height / 2 - padY).attr("width", bbox.width + padX * 2).attr("height", bbox.height + padY * 2);
      });

    // Collapse/expand chevron for tree-layout nodes that have descendants.
    if (layout === "tree") {
      const collapseHandle = node
        .filter((d) => (childrenOf.get(d.id) || []).length > 0)
        .append("g")
        .attr("class", "collapse-handle")
        .attr("transform", "translate(0,30)")
        .style("cursor", "pointer")
        .on("click", (ev, d) => {
          ev.stopPropagation();
          setCollapsedIds((prev) => {
            const next = new Set(prev);
            if (next.has(d.id)) next.delete(d.id);
            else next.add(d.id);
            return next;
          });
        });
      collapseHandle
        .append("circle")
        .attr("r", 9)
        .attr("fill", "#FFFFFF")
        .attr("stroke", "#1B3A2C")
        .attr("stroke-width", 1.5);
      collapseHandle
        .append("text")
        .text((d) => (collapsedIds.has(d.id) ? "+" : "−"))
        .attr("text-anchor", "middle")
        .attr("dy", "0.35em")
        .attr("fill", "#1B3A2C")
        .style("font-family", "Manrope, sans-serif")
        .style("font-size", "13px")
        .style("font-weight", 700);
    }

    if (layout === "web" && sim) {
      sim.on("tick", () => {
        link
          .attr("x1", (d) => d.source.x).attr("y1", (d) => d.source.y)
          .attr("x2", (d) => d.target.x).attr("y2", (d) => d.target.y);
        node.attr("transform", (d) => `translate(${d.x},${d.y})`);
      });
      return () => sim.stop();
    } else {
      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    }
  }, [data, onSelect, layout, collapsedIds]);

  return (
    <div ref={containerRef} className="relative w-full bg-ivory rounded-xl border border-sage overflow-hidden" data-testid="connection-map-canvas">
      <svg ref={svgRef} className="w-full" style={{ height: layout === "tree" ? 720 : 600 }} />
      {layout === "tree" && (
        <div className="absolute bottom-3 left-4 text-[11px] uppercase tracking-widest text-sageGray bg-white/90 rounded-full px-3 py-1.5 border border-sage shadow-sm pointer-events-none" data-testid="map-pan-hint">
          drag to pan · scroll to zoom · ± on a node to collapse
        </div>
      )}
      <div className="absolute top-3 right-4 flex flex-col items-end gap-2">
        <button
          type="button"
          onClick={() => setLegendOpen((v) => !v)}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-sage shadow-sm hover:bg-cream text-forest"
          aria-label={legendOpen ? "Hide legend" : "Show legend"}
          data-testid="map-legend-toggle"
        >
          {/* Inline key icon — keeps the bundle small */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="15" r="4" />
            <path d="M10.85 12.15 19 4" />
            <path d="m18 5 1 1" />
            <path d="m14 9 1 1" />
          </svg>
        </button>
        {legendOpen && (
          <div className="flex flex-col gap-1.5 text-xs bg-white/95 rounded-lg px-3 py-2.5 border border-sage shadow-sm" data-testid="map-legend-panel">
            <p className="font-display text-xs uppercase tracking-widest text-forest/70 mb-0.5">Edges</p>
            <div className="flex items-center gap-2"><span className="w-3 h-0.5 bg-forest" /> parent/child</div>
            <div className="flex items-center gap-2"><span className="w-3 h-0.5 bg-gold" /> spouse</div>
            <div className="flex items-center gap-2"><span className="w-3 h-0.5 border-t border-dashed border-sageGray" /> sibling</div>
            <p className="font-display text-xs uppercase tracking-widest text-forest/70 mt-2 mb-0.5">Branch</p>
            <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full border-2 border-gold bg-white" /> paternal</div>
            <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full border-2 border-forest bg-white" /> maternal</div>
            <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full border-2 border-sageGray bg-white" /> both / unset</div>
            <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full border-2 border-[#E89C3F] bg-[#FFF8EC]" /> not yet joined</div>
            <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full bg-white shadow-[0_0_0_2px_#E89C3F,0_0_8px_2px_rgba(232,156,63,0.5)]" /> in memoriam</div>
          </div>
        )}
      </div>
    </div>
  );
}
